import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { listAdminPosts, deletePost } from "@/lib/posts.functions";
import { generateArticleAiSummary } from "@/lib/ai-summaries.functions";

const searchSchema = z.object({
  q: z.string().optional(),
  slug: z.string().optional(),
  action: z.enum(["sources", "translate-en", "seo-meta"]).optional(),
  apply: z.preprocess((v) => v === true || v === "true", z.boolean()).optional(),
});

export const Route = createFileRoute("/admin/yazilar")({
  head: () => ({
    meta: [{ title: "Yazılar · Admin" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  validateSearch: searchSchema,
  // Merged into /admin/icerik. Action links (?action=…, ?slug=…, ?q=…) keep working here.
  beforeLoad: ({ search }) => {
    if (!search.q && !search.slug && !search.action) throw redirect({ to: "/admin/icerik", replace: true });
  },
  component: AdminPostsList,
});

const ACTION_COPY: Record<NonNullable<z.infer<typeof searchSchema>["action"]>, { title: string; body: string; cta: string; tool: "sources" | "translate-en" | "seo-meta" }> = {
  sources: {
    title: "Kaynakça aksiyonu",
    body: "İlgili yazıyı seçtiğinizde AI kaynak önerme aracı otomatik açılır ve doğrulanmış dış referansları hazırlamaya başlar.",
    cta: "Kaynak öneriyi aç",
    tool: "sources",
  },
  "translate-en": {
    title: "EN çeviri aksiyonu",
    body: "İlgili yazıyı seçtiğinizde editör EN sekmesine geçer ve Türkçe içerikten İngilizce taslak üretimini başlatır.",
    cta: "EN üretimi başlat",
    tool: "translate-en",
  },
  "seo-meta": {
    title: "CTR / meta aksiyonu",
    body: "İlgili yazıyı seçtiğinizde başlık ve kısa özet alanlarına odaklanan düzenleme modu açılır.",
    cta: "Başlık & meta düzenle",
    tool: "seo-meta",
  },
};

function RegenerateOneButton({ id }: { id: string }) {
  const run = useServerFn(generateArticleAiSummary);
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const onClick = async () => {
    setState("loading");
    try {
      await run({ data: { id } });
      setState("done");
    } catch {
      setState("error");
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={state === "loading"}
      className="text-xs text-ink/85 hover:text-clay disabled:opacity-50"
      title="AI özet üret"
    >
      {state === "loading" ? "…" : state === "done" ? "✓ AI özet" : state === "error" ? "Hata" : "AI özet"}
    </button>
  );
}

function AdminPostsList() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const action = search.action as keyof typeof ACTION_COPY | undefined;
  const targetSlug = search.slug?.trim();
  const list = useServerFn(listAdminPosts);
  const del = useServerFn(deletePost);
  const [q, setQ] = useState(search.q ?? "");
  const autoOpened = useRef(false);

  useEffect(() => {
    setQ(search.q ?? "");
  }, [search.q]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-posts"],
    queryFn: () => list(),
  });

  const onDelete = async (id: string, title: string) => {
    if (!confirm(`"${title}" silinsin mi?`)) return;
    await del({ data: { id } });
    refetch();
  };

  const posts = data?.posts ?? [];
  const filtered = useMemo(() => {
    if (targetSlug) return posts.filter((p) => p.slug === targetSlug);
    const term = q.trim().toLowerCase();
    if (!term) return posts;
    return posts.filter((p) =>
      [p.title_tr, p.title_en, p.slug, p.category].filter(Boolean).some((v) => v!.toLowerCase().includes(term)),
    );
  }, [posts, q, targetSlug]);

  useEffect(() => {
    if (!action || autoOpened.current || isLoading || filtered.length !== 1) return;
    if (!targetSlug && !q.trim()) return;
    autoOpened.current = true;
    navigate({
      to: "/admin/$id",
      params: { id: filtered[0].id },
      search: { tool: ACTION_COPY[action].tool, apply: search.apply === true },
    });
  }, [action, filtered, isLoading, navigate, q, search.apply, targetSlug]);

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl">Yazılar</h1>
        <Link
          to="/admin/new"
          className="px-4 py-2 bg-ink text-paper rounded-full text-xs uppercase tracking-[0.2em]"
        >
          + Yeni
        </Link>
      </div>

      <div className="mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Başlık, slug ya da kategoriye göre ara…"
          className="w-full max-w-md text-sm border border-ink/15 rounded px-3 py-2 bg-white"
        />
        {q && (
          <span className="ml-2 text-xs text-ink/60">
            {filtered.length}/{posts.length}
          </span>
        )}
        {targetSlug && (
          <span className="ml-2 text-xs text-ink/60">
            Hedef: {targetSlug} · {filtered.length}/{posts.length}
          </span>
        )}
      </div>

      {action && (
        <div className="mb-5 rounded border border-clay/30 bg-clay/5 p-4 text-sm">
          <div className="font-serif text-lg">{ACTION_COPY[action].title}</div>
          <p className="mt-1 text-ink/65 leading-relaxed">{ACTION_COPY[action].body}</p>
          {(targetSlug || q) && (
            <p className="mt-2 text-xs text-ink/50">
              Hedef: “{targetSlug || q}”{search.apply ? " · otomatik uygula" : ""}
            </p>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-ink/60">Yükleniyor...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-ink/60">
          {q ? "Eşleşen yazı yok." : "Henüz yazı yok. + Yeni'ye tıklayın."}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {filtered.map((p) => (
            <li key={p.id} className="py-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded ${
                      p.published ? "bg-moss/10 text-moss" : "bg-ink/10 text-ink/60"
                    }`}
                  >
                    {p.published ? "Yayında" : "Taslak"}
                  </span>
                  <span className="text-xs text-ink/85">{p.category}</span>
                </div>
                <button
                  onClick={() =>
                    navigate({
                      to: "/admin/$id",
                      params: { id: p.id },
                      search: action ? { tool: ACTION_COPY[action].tool, apply: search.apply === true } : {},
                    })
                  }
                  className="font-serif text-lg mt-1 text-left hover:text-clay truncate block w-full"
                >
                  {p.title_tr || p.title_en || p.slug}
                </button>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                {action && (
                  <Link
                    to="/admin/$id"
                    params={{ id: p.id }}
                    search={{ tool: ACTION_COPY[action].tool, apply: search.apply === true }}
                    className="text-xs px-3 py-1.5 rounded-full bg-clay text-paper hover:bg-clay/90"
                  >
                    {ACTION_COPY[action].cta}
                  </Link>
                )}
                <RegenerateOneButton id={p.id} />
                <button
                  onClick={() => onDelete(p.id, p.title_tr || p.title_en || p.slug)}
                  className="text-xs text-ink/85 hover:text-clay"
                >
                  Sil
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
